import { BehaviorSubject, Observable, filter, map, skip, switchMap, take } from 'rxjs';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, TransferState } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { ConfigInitializerService, LanguageService, OccEndpointsService } from '@spartacus/core';

import { BrandsEnum } from '@seb/web-design-system/core';

import { ConfigurationService } from './configuration.service';
import { UrlMapping, UrlMappings } from '../models';
import { URL_MAPPING_KEY } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class UrlMappingService {
  private urlMappingsSubject = new BehaviorSubject<UrlMapping[]>(undefined);
  private get isUrlMappingLog(): boolean {
    return this.configService.config.isUrlMappingLog;
  }

  constructor(
    private occEndpointsService: OccEndpointsService,
    private http: HttpClient,
    private configInitializerService: ConfigInitializerService,
    private location: Location,
    private router: Router,
    private languageService: LanguageService,
    private transferState: TransferState,
    private configService: ConfigurationService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(URL_MAPPING_KEY)) {
      this.urlMappingsSubject.next(this.transferState.get(URL_MAPPING_KEY, []));
      this.transferState.remove(URL_MAPPING_KEY);
    } else {
      this.configInitializerService.getStable('backend', 'context').pipe(
        switchMap(() => this.loadUrlMappings())
      ).subscribe();
    }

    this.languageService.getActive().pipe(
      filter((l) => !!l),
      skip(1),
      switchMap(() => this.loadUrlMappings())
    ).subscribe();
  }

  getUrlMappingByCode(url: string): Observable<UrlMapping> {
    if (this.isUrlMappingLog) {
      console.log('url to translate by code: ', url);
    }

    return this.urlMappingsSubject.asObservable().pipe(
      filter((mappings) => !!mappings),
      map((mappings) => {
        let foundMapping: UrlMapping;
        const urlSegments = url.split('/').map((urlFragment) => urlFragment.split('?')[0]);
        while (!foundMapping && urlSegments.length > 1) {
          foundMapping = mappings.find((mapping) => mapping.code === urlSegments.join('/') && !!mapping.value);
          urlSegments.pop();
        }
        if (this.isUrlMappingLog) {
          if (foundMapping?.code || foundMapping?.value) {
            console.log('url translated by code, value: ', foundMapping?.code, 'code: ', foundMapping?.value);
          } else console.log('url translated by code not found ');
        }
        return foundMapping;
      })
    );
  }

  translateUrl(urlMapping: UrlMapping): void {
    const translatedUrl = this.router.url.replace(urlMapping.code, urlMapping.value);
    this.location.replaceState(translatedUrl);
  }

  getUrlMappingByTranslation(url: string, brands: BrandsEnum[]): Observable<UrlMapping> {
    if (this.isUrlMappingLog) {
      console.log('url to translate by translation: ', url);
    }

    url = brands.length > 0 ? this.removeBrandFromUrl(url, brands) : url;

    return this.urlMappingsSubject.asObservable().pipe(
      filter((mappings) => !!mappings),
      take(1),
      map((mappings) => {
        let foundMapping: UrlMapping;
        const urlSegments = url.split('/').map((urlFragment) => urlFragment.split('?')[0]);
        while (!foundMapping && urlSegments.length > 1) {
          foundMapping = mappings.find((mapping) => mapping.value === urlSegments.join('/'));
          urlSegments.pop();
        }
        if (this.isUrlMappingLog) {
          if (foundMapping?.code || foundMapping?.value) {
            console.log('url translated by Translation, code: ', foundMapping?.code, 'value: ', foundMapping?.value);
          } else console.log('url translated by Translation not found ');
        }
        return foundMapping;
      })
    );
  }

  private loadUrlMappings(): Observable<boolean> {
    const url = this.occEndpointsService.buildUrl('urlMappings');

    return this.http.get<UrlMappings>(url).pipe(
      map((response) => {
        if (response) {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(URL_MAPPING_KEY, response.urlMappings);
          }
          this.urlMappingsSubject.next(response.urlMappings);
          return true;
        }
        return false;
      })
    );
  }

  private removeBrandFromUrl(url: string, brands: BrandsEnum[]): string {
    for (const brand of brands) {
      const fragment = '/' + brand.toLowerCase();
      if (url.includes(fragment)) {
        return url.split(fragment)[1] || '';
      }
    }

    return url;
  }
}
